import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { HiArrowLeft, HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { useAlertTypes } from '../../hooks/queries/useAlertTypes';
import { useAlertGuidances } from '../../hooks/queries/useAlertGuidance';
import type { AlertType } from '../../services/alertTypes.service';

interface ProceduresLibraryProps {
  onBack: () => void;
}

// ── Guidance for one alert type ──────────────────────────────────────────────

function AlertTypeGuidance({ alertType }: { alertType: AlertType }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useAlertGuidances(alertType.id);

  const guidances = data?.results ?? [];
  const hasGuidance = guidances.length > 0;

  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      overflow="hidden"
    >
      <Flex
        as="button"
        w="100%"
        align="center"
        justify="space-between"
        p={4}
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        _hover={{ bg: 'surface.secondary' }}
      >
        <Flex align="center" gap={3}>
          <Box>
            <Text fontWeight={600} color="text.primary" textAlign="left">
              {alertType.name}
            </Text>
            <Text fontSize="xs" color="text.secondary" textAlign="left">
              {alertType.urgency === 'emergency'
                ? t('protocols.emergency', 'Emergency')
                : alertType.urgency === 'attention'
                  ? t('protocols.attention', 'Attention')
                  : t('protocols.followUp', 'Follow-up')}
              {!hasGuidance && ` — ${t('documents.noProceduresConfigured', 'No procedures configured')}`}
            </Text>
          </Box>
        </Flex>
        {hasGuidance &&
          (expanded ? (
            <HiChevronDown size={20} />
          ) : (
            <HiChevronRight size={20} />
          ))}
      </Flex>

      {expanded && (
        <Box px={4} pb={4}>
          {isLoading && <Spinner size="sm" />}

          {guidances.map((guidance) => (
            <Box key={guidance.id} mb={4}>
              <Text
                fontSize="sm"
                fontWeight={600}
                color="text.primary"
                mb={2}
              >
                {guidance.title}
                {guidance.is_default && (
                  <Text as="span" fontSize="xs" color="text.secondary" ml={2}>
                    ({t('common.default', 'Default')})
                  </Text>
                )}
              </Text>

              {guidance.steps && guidance.steps.length > 0 ? (
                <VStack gap={1} align="stretch" pl={2}>
                  {guidance.steps
                    .sort((a, b) => a.order - b.order)
                    .map((step, i) =>
                      step.row_type === 'header' ? (
                        <Text
                          key={i}
                          fontSize="sm"
                          fontWeight={600}
                          color="text.primary"
                          mt={2}
                        >
                          {step.title}
                        </Text>
                      ) : (
                        <Flex key={i} align="flex-start" gap={2}>
                          <Text
                            fontSize="sm"
                            color={
                              step.is_tick_required
                                ? 'text.primary'
                                : 'text.secondary'
                            }
                            minW="18px"
                          >
                            {step.is_tick_required ? '☐' : '•'}
                          </Text>
                          <Box>
                            <Text fontSize="sm" color="text.primary">
                              {step.title}
                            </Text>
                            {step.description && (
                              <Text fontSize="xs" color="text.secondary">
                                {step.description}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      ),
                    )}
                </VStack>
              ) : (
                <Text fontSize="sm" color="text.secondary">
                  {t('documents.noStepsDefined', 'No steps defined.')}
                </Text>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export function ProceduresLibrary({ onBack }: ProceduresLibraryProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useAlertTypes({
    page_size: 200,
    is_active: true,
  });

  const alertTypes = data?.results ?? [];

  // Group by urgency
  const emergency = alertTypes.filter((t) => t.urgency === 'emergency');
  const attention = alertTypes.filter((t) => t.urgency === 'attention');
  const follow = alertTypes.filter((t) => t.urgency === 'follow');

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex align="center" gap={3} mb={6}>
        <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h2" fontSize="lg" fontWeight={600} color="text.primary">
            {t('documents.emergencyProcedures', 'Emergency Procedures')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.guidanceChecklists', 'Guidance checklists by alert type')}
          </Text>
        </Box>
      </Flex>

      {isLoading && (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      )}

      {error && (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadAlertTypes', 'Failed to load alert types')}
          </Text>
        </Center>
      )}

      {!isLoading && !error && alertTypes.length === 0 && (
        <Center py={12}>
          <Text color="text.secondary">{t('documents.noAlertTypesConfigured', 'No alert types configured.')}</Text>
        </Center>
      )}

      {alertTypes.length > 0 && (
        <VStack gap={6} align="stretch">
          {emergency.length > 0 && (
            <Box>
              <Text
                fontWeight={600}
                color="text.danger"
                fontSize="sm"
                mb={3}
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {t('protocols.emergency', 'Emergency')}
              </Text>
              <VStack gap={2} align="stretch">
                {emergency.map((t) => (
                  <AlertTypeGuidance key={t.id} alertType={t} />
                ))}
              </VStack>
            </Box>
          )}

          {attention.length > 0 && (
            <Box>
              <Text
                fontWeight={600}
                color="yellow.600"
                fontSize="sm"
                mb={3}
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {t('protocols.attention', 'Attention')}
              </Text>
              <VStack gap={2} align="stretch">
                {attention.map((t) => (
                  <AlertTypeGuidance key={t.id} alertType={t} />
                ))}
              </VStack>
            </Box>
          )}

          {follow.length > 0 && (
            <Box>
              <Text
                fontWeight={600}
                color="text.secondary"
                fontSize="sm"
                mb={3}
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {t('protocols.followUp', 'Follow-up')}
              </Text>
              <VStack gap={2} align="stretch">
                {follow.map((t) => (
                  <AlertTypeGuidance key={t.id} alertType={t} />
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
}
